import StoryStorage from "../Storage/StoryStorage";
import LevelConfigStorage from "../Storage/LevelConfigStorage";
import ExperiencePoints from "../Character/ExperiencePoints";
import CharacterAttributesGUI from "../GUI/CharacterAttributesGUI";
import CharacterAttributes from "../Character/CharacterAttributes";
import CharacterStorage from "../Storage/CharacterStorage";

export class WinLevelScene extends Phaser.Scene {
  private mBackground: Phaser.GameObjects.Image;

  private mWinningText: Phaser.GameObjects.Text;
  private mRankText: Phaser.GameObjects.Text;
  private mGoBackText: Phaser.GameObjects.Text;
  private mGoldEarned: number;
  private mExpEarned: number;
  private mLevelWonName: string;

  private mMobMarkerPercent: number;
  private mStoryStorage: StoryStorage;
  private mLevelConfigStorage: LevelConfigStorage;
  private mCharacterStorage: CharacterStorage;

  // Animation thingies
  private mTimerEvent: Phaser.Time.TimerEvent;
  private mGoldShown: number = 0;
  private mExpShown: number = 0;
  private mGoldAnimationToAdd: number = 0;
  private mExpAnimationToAdd: number = 0;
  private mFinishGoldAnimation: boolean = false;
  private mExperiencePoints: ExperiencePoints;
  private mAttribGUI: CharacterAttributesGUI;
  private mLevelsGained: number = 0;
  private mLevelUpText: Phaser.GameObjects.Text;

  constructor() {
    super("WinLevelScene");
    this.mStoryStorage = new StoryStorage();
    this.mLevelConfigStorage = new LevelConfigStorage();
    this.mCharacterStorage = new CharacterStorage();
  }

  init(obj) {
    this.mGoldEarned = obj.EarnedGold;
    this.mExpEarned = obj.EarnedExp ?? 0;
    this.mLevelWonName = obj.LevelName;
    this.mMobMarkerPercent = obj.MobMarkerPercent;
    this.mExperiencePoints = obj.ExperiencePoints;

    this.mGoldShown = 0;
    this.mExpShown = 0;
    this.mGoldAnimationToAdd = this.mGoldEarned > 100 ? 5 : 1;
    this.mExpAnimationToAdd = this.mExpEarned > 100 ? 5 : 1;

    this.mLevelConfigStorage.AddLevelCompleted(this.mLevelWonName);

    const newLevels = this.mExperiencePoints.AddExp(this.mExpEarned);
    this.mLevelsGained = newLevels;
    const apToAdd = newLevels * CharacterAttributes.AttributePointsOnLevel;
    const totalAP =
      this.mCharacterStorage.GetAvailableAttributePoints() + apToAdd;
    this.mCharacterStorage.SetAvailableAttributePoints(totalAP);
    obj.CharacterAttributes.AvailableAttributePoints = totalAP;

    const spToAdd = newLevels * CharacterAttributes.SkillPointsOnLevel;
    const totalSP =
      this.mCharacterStorage.GetAvailableSkillPoints() + spToAdd;
    this.mCharacterStorage.SetAvailableSkillPoints(totalSP);
    obj.CharacterAttributes.AvailableSkillPoints = totalSP;

    this.mAttribGUI = new CharacterAttributesGUI(obj.CharacterAttributes);
  }

  preload() {
    this.load.image("winBackground", "images/Scenes/WinScene.png");
    this.mAttribGUI.preload(this);
  }

  create() {
    console.log(`Gold Won ${this.mGoldEarned}`);

    let width = this.cameras.main.width;
    let height = this.cameras.main.height;
    this.mBackground = this.add.image(width / 2, height / 2, "winBackground");
    this.mBackground.setDisplaySize(width, height);
    this.mBackground.setDepth(-1);

    let rank = this.rankWinBasedOnMarker();

    this.mRankText = this.add.text(800, 150, `${rank}`, {
      fontSize: "120px",
      color: this.getColorForRank(rank),
    });

    this.mRankText.scale = 0;
    this.mWinningText = this.add.text(100, 100, [`+0 gold`, "+0 exp", this.buildExpProgressLine(this)], {
      fontSize: "50px",
      color: `0xffe842`,
    });

    this.mLevelUpText = this.add.text(780, 370, "LEVEL UP!", {
      fontSize: "64px",
      color: "#FFD700",
      fontFamily: "monospace",
      fontStyle: "bold",
    });
    this.mLevelUpText.setScale(0);

    this.mGoBackText = this.add
      .text(100, height - 60, "Back To Story", {
        fontSize: "50px",
        color: `0xffffff`,
      })
      .setInteractive()
      .on("pointerdown", () => {
        this.cameras.main.fade(100);
        this.time.delayedCall(100, () => {
          this.scene.transition({
            target: "SelectLevelScene",
            duration: 200,
            sleep: true,
            remove: false,
          });
        });
      });

    console.log("Running timer");

    this.mTimerEvent = this.time.addEvent({
      delay: 25,
      loop: true,
      callback: () => {
        this.updateGoldAnimation(this);
      },
      args: [this],
    });
    this.mStoryStorage.AddExp(this.mExpEarned);
    this.mStoryStorage.AddMoney(this.mGoldEarned);

    this.mAttribGUI.create(
      this,
      this.mWinningText.x,
      this.mWinningText.y + this.mWinningText.height + 10
    );
  }

  private buildExpProgressLine(scene: WinLevelScene): string {
    const expBeforeEarning = scene.mExperiencePoints.ExperiencePoints - scene.mExpEarned;
    const animatedExp = expBeforeEarning + scene.mExpShown;
    const nextThreshold = scene.mExperiencePoints.GetNextLevelThreshold();
    const levelStr = `Lv.${scene.mExperiencePoints.Level}`;
    return nextThreshold != null
      ? `${levelStr}  ${animatedExp} / ${nextThreshold} XP`
      : `${levelStr}  MAX LEVEL`;
  }

  updateGoldAnimation(scene: WinLevelScene) {
    scene.mGoldShown += scene.mGoldAnimationToAdd;

    if (scene.mGoldShown + scene.mGoldAnimationToAdd >= scene.mGoldEarned) {
      scene.mGoldShown = scene.mGoldEarned;

      scene.mTimerEvent.remove();

      scene.mTimerEvent = scene.time.addEvent({
        delay: 25,
        loop: true,
        callback: () => {
          this.updateExpAnimation(scene);
        },
        args: [this],
      });
    }

    scene.mWinningText.setText([
      `+${scene.mGoldShown} gold`,
      `+${scene.mExpShown} exp`,
      scene.buildExpProgressLine(scene),
    ]);
  }

  updateExpAnimation(scene: WinLevelScene) {
    scene.mExpShown += scene.mExpAnimationToAdd;

    if (scene.mExpShown + scene.mExpAnimationToAdd >= scene.mExpEarned) {
      scene.mExpShown = scene.mExpEarned;

      scene.mTimerEvent.remove();

      scene.mRankText.scale = 5;
      scene.add.tween({
        targets: scene.mRankText,
        scale: 1,
        duration: 1000,
        ease: Phaser.Math.Easing.Bounce.Out,
      });

      if (scene.mLevelsGained > 0) {
        scene.mLevelUpText.setScale(4);
        scene.add.tween({
          targets: scene.mLevelUpText,
          scale: 1,
          duration: 900,
          ease: Phaser.Math.Easing.Bounce.Out,
        });
      }
    }
    scene.mWinningText.setText([
      `+${scene.mGoldShown} gold`,
      `+${scene.mExpShown} exp`,
      scene.buildExpProgressLine(scene),
    ]);
  }

  //TODO: Add additional info like how many monsters were defeated, etc...
  rankWinBasedOnMarker() {
    if (this.mMobMarkerPercent > 90 && this.mMobMarkerPercent <= 100) {
      return this.mMobMarkerPercent > 95 ? "A+" : "A";
    } else if (this.mMobMarkerPercent > 70 && this.mMobMarkerPercent <= 90) {
      return this.mMobMarkerPercent > 80 ? "B+" : "B";
    } else if (this.mMobMarkerPercent > 50 && this.mMobMarkerPercent <= 70) {
      return this.mMobMarkerPercent > 60 ? "C+" : "C";
    } else if (this.mMobMarkerPercent > 20 && this.mMobMarkerPercent <= 50) {
      return this.mMobMarkerPercent > 40 ? "D+" : "D";
    } else {
      return "F";
    }
  }

  getColorForRank(rank) {
    switch (rank) {
      case "A+":
        return "0X4CAF50"; // Bright green for excellent
      case "A":
        return "0X8BC34A"; // Light green
      case "B+":
        return "0XFFEB3B"; // Yellow
      case "B":
        return "0XFFC107"; // Amber
      case "C+":
        return "0XFF9800"; // Orange
      case "C":
        return "0XFF5722"; // Deep orange
      case "D+":
        return "0XF44336"; // Red
      case "D":
        return "0XE91E63"; // Bright pink-red
      case "F":
        return "0X9E9E9E"; // Grey for fail
      default:
        return "0X000000"; // Black for undefined ranks
    }
  }
}
